import { useCallback, useEffect, useState } from 'react'
import type { Session } from '@supabase/supabase-js'
import { supabase, missingSupabaseConfig } from '../lib/supabase'
import { useGetBoard, useClaimInvite } from '../hooks/backend/driver'
import { Chip } from '../components/interviews/Chip'
import {
  gateLabel,
  gateAdvice,
  STANDING_TEXT,
  rand,
  fixed,
  percent,
  weekLabel,
  type Standing,
  type GateVerdict,
  type Coaching,
  type ZoneGuideData,
} from '../utils/competition'
import { CoachingCard } from '../components/performance/CoachingCard'
import { ServiceHoursCard } from '../components/payroll/ServiceHoursCard'
import { ZoneGuide } from '../components/performance/ZoneGuide'
import { MyFinesCard } from './MyFinesCard'

/**
 * The driver board, at /board.
 *
 * An active driver signs in with his own Google account and sees where he
 * stands in this week's competition: his standing, what is blocking him and
 * what to do about it, and the board of everyone else by first name.
 *
 * His Google account becomes his only through the one-time link a Driver
 * Servant sends him (?invite=CODE). That is deliberate and it is the whole
 * security model: docs/DRIVER-AUTH.md explains why a Google email is never
 * matched to a driver. Everything shown comes from driver_portal_board_v1, which
 * returns other drivers' names, ranks, earnings and standings only.
 */

const INVITE_KEY = 'fleetly.board.invite'

function readInvite(): string | null {
  const fromUrl = new URLSearchParams(window.location.search).get('invite')
  if (fromUrl) {
    try {
      sessionStorage.setItem(INVITE_KEY, fromUrl)
    } catch {
      /* private mode: the code is still in the redirect URL */
    }
    return fromUrl
  }
  try {
    return sessionStorage.getItem(INVITE_KEY)
  } catch {
    return null
  }
}

function clearInvite() {
  try {
    sessionStorage.removeItem(INVITE_KEY)
  } catch {
    /* nothing to clear */
  }
  if (window.location.search) window.history.replaceState(null, '', '/board')
}

type BoardEntry = {
  rank: number | null
  name: string
  driver_id?: string | null
  earnings: number | string | null
  shifts: number | null
  standing: Standing
  prize: number | string | null
  is_me: boolean
}

type MyRow = {
  driver_name?: string | null
  no_shifts?: boolean
  standing?: Standing
  earnings_rank?: number | null
  total_earnings_zar?: number | string | null
  shifts?: number | null
  earnings_per_shift_zar?: number | string | null
  online_hours_avg?: number | string | null
  acceptance_avg?: number | string | null
  earnings_per_trip_zar?: number | string | null
  l_per_100km?: number | string | null
  fuel_standard_l_per_100km?: number | string | null
  rating?: number | string | null
  prize_zar?: number | string | null
  gates?: Record<string, GateVerdict> | null
  failed_gates?: string[] | null
  pending_gates?: string[] | null
}

type DriverBoard =
  | { state: 'not_linked' }
  | { state: 'inactive' }
  | {
      state: 'ok' | 'staff'
      viewer?: string | null
      week_start: string
      week_end: string
      week_open: boolean
      status: string
      prizes: (number | string | null)[]
      weeks: string[]
      me: MyRow | null
      board: BoardEntry[]
      coaching?: Coaching | null
      zone_guide?: ZoneGuideData | null
    }

export default function DriverBoardApp() {
  const [session, setSession] = useState<Session | null>(null)
  const [checked, setChecked] = useState(false)

  useEffect(() => {
    if (missingSupabaseConfig) {
      setChecked(true)
      return
    }
    let active = true
    supabase.auth.getSession().then(({ data }) => {
      if (!active) return
      setSession(data.session)
      setChecked(true)
    })
    const { data: sub } = supabase.auth.onAuthStateChange((_e, next) => {
      setSession(next)
      setChecked(true)
    })
    return () => {
      active = false
      sub.subscription.unsubscribe()
    }
  }, [])

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="flex items-center gap-3 border-b border-border bg-card px-4 py-3">
        <img src="/fleetly-mark.svg" alt="" aria-hidden="true" width={32} height={32} className="h-8 w-8" />
        <div className="leading-tight">
          <p className="text-base font-semibold">Fleetly driver board</p>
          <p className="text-[11px] font-medium uppercase tracking-wide text-[hsl(var(--brand))]">Driving Change</p>
        </div>
        {session && (
          <button
            type="button"
            onClick={() => supabase.auth.signOut()}
            className="ml-auto rounded-md border border-border px-3 py-1 text-xs text-muted-foreground"
          >
            Sign out
          </button>
        )}
      </header>
      <main className="mx-auto max-w-2xl p-4">
        {missingSupabaseConfig ? (
          <Notice title="The board is not configured">This build has no sign-in configuration.</Notice>
        ) : !checked ? (
          <p className="p-6 text-center text-sm text-muted-foreground">Checking your sign-in…</p>
        ) : !session ? (
          <DriverSignIn />
        ) : (
          <SignedInBoard email={session.user?.email ?? null} />
        )}
      </main>
    </div>
  )
}

function DriverSignIn() {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const invite = readInvite()

  async function signIn() {
    setBusy(true)
    setError(null)
    const { error: err } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/board${invite ? `?invite=${encodeURIComponent(invite)}` : ''}`,
        queryParams: { prompt: 'select_account' },
      },
    })
    if (err) {
      setError(err.message)
      setBusy(false)
    }
  }

  return (
    <div className="mt-6 rounded-xl border border-border bg-card p-6">
      <h1 className="text-lg font-semibold">See where you stand this week</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        The weekly competition pays R2,000, R1,000 and R500 to the top earners who meet every rule. Sign in with your
        Google account to see your standing and anything that is blocking you.
      </p>
      {!invite && (
        <p className="mt-3 text-sm text-muted-foreground">
          First time? Use the sign-in link your Driver Servant sent you, so your account is connected to you.
        </p>
      )}
      <button
        type="button"
        onClick={signIn}
        disabled={busy}
        className="mt-5 w-full rounded-md bg-[hsl(var(--brand))] px-4 py-3 text-sm font-medium text-[hsl(var(--brand-foreground))] disabled:opacity-60"
      >
        {busy ? 'Opening Google…' : 'Sign in with Google'}
      </button>
      {error && <p className="mt-3 text-xs text-destructive">{error}</p>}
    </div>
  )
}

function SignedInBoard({ email }: { email: string | null }) {
  const board = useGetBoard()
  const claim = useClaimInvite()
  const [claimError, setClaimError] = useState<string | null>(null)
  const [claimDone, setClaimDone] = useState(false)
  const [week, setWeek] = useState<string | null>(null)
  // Staff only: which driver's own screen to open. The server ignores it for a
  // driver, who only ever sees his own.
  const [viewDriver, setViewDriver] = useState<string | null>(null)
  const loadBoard = board.trigger
  const runClaim = claim.trigger

  const load = useCallback(() => {
    const params: Record<string, string> = {}
    if (week) params.week_start = week
    if (viewDriver) params.driver_id = viewDriver
    loadBoard(params, { skipCache: true }).catch(() => {
      // useBackend has put the message in board.error for the screen.
    })
  }, [loadBoard, week, viewDriver])

  useEffect(() => {
    const invite = readInvite()
    if (!invite || claimDone) {
      load()
      return
    }
    // The link is claimed once, then forgotten, whatever the answer: a refused
    // link must not be retried on every visit.
    Promise.resolve(runClaim({ code: invite }))
      .catch((e: unknown) => setClaimError(e instanceof Error ? e.message : String(e)))
      .finally(() => {
        clearInvite()
        setClaimDone(true)
        load()
      })
  }, [runClaim, load, claimDone])

  const data = board.data as DriverBoard | null | undefined

  if (board.error) return <Notice title="The board could not load">{String(board.error)} Try again in a minute.</Notice>
  if (!data) return <p className="p-6 text-center text-sm text-muted-foreground">Loading your board…</p>

  if (data.state === 'not_linked') {
    return (
      <Notice title="Your account is not connected yet">
        {claimError ? `${claimError} ` : ''}You are signed in as {email ?? 'an unknown account'}, which is not connected to
        a Fleetly driver. Ask your Driver Servant for your board sign-in link, open it on this phone, and sign in with this
        same Google account.
      </Notice>
    )
  }
  if (data.state === 'inactive') {
    return <Notice title="The board is for active drivers">Your driver account is not active. Speak to your Driver Servant.</Notice>
  }

  const isStaff = data.state === 'staff'
  const me: MyRow | null = data.me
  const standing = me?.standing ? STANDING_TEXT[me.standing] : null
  const failed = me?.failed_gates ?? []
  const pending = me?.pending_gates ?? []
  const passed = Object.entries(me?.gates ?? {})
    .filter(([, v]) => v === 'pass')
    .map(([k]) => k)

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-sm text-muted-foreground">
          {weekLabel(data.week_start, data.week_end)}
          {data.week_open ? ', week in progress, updated every hour' : ', week closed'}
        </p>
        {data.weeks.length > 1 && (
          <select
            aria-label="Week"
            className="ml-auto rounded-md border border-border bg-background px-2 py-1 text-sm"
            value={data.week_start}
            onChange={(e) => setWeek(e.target.value)}
          >
            {data.weeks.map((w) => (
              <option key={w} value={w}>
                Week of {w.slice(8, 10)}/{w.slice(5, 7)}
              </option>
            ))}
          </select>
        )}
      </div>

      {isStaff && (
        <section className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm dark:border-amber-900 dark:bg-amber-950/40">
          <p className="font-medium">Staff view{data.viewer ? `, signed in as ${data.viewer}` : ''}</p>
          <p className="mt-1 text-muted-foreground">
            Pick a driver to see his screen exactly as he sees it. Drivers see each other by first name and initial only.
          </p>
          <select
            aria-label="Driver"
            className="mt-2 w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm"
            value={viewDriver ?? ''}
            onChange={(e) => setViewDriver(e.target.value || null)}
          >
            <option value="">Choose a driver</option>
            {[...data.board]
              .filter((b) => b.driver_id)
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((b) => (
                <option key={b.driver_id!} value={b.driver_id!}>
                  {b.name}
                </option>
              ))}
          </select>
        </section>
      )}

      {me && (
      <section className="rounded-xl border border-border bg-card p-4">
        <p className="text-sm text-muted-foreground">{me.driver_name ?? 'You'}</p>
        {me.no_shifts ? (
          <p className="mt-2 text-sm">You have no shifts this week yet. Your standing appears after your first shift.</p>
        ) : (
          <>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              {standing && <Chip tone={standing.tone}>{standing.label}</Chip>}
              <span className="text-2xl font-semibold tabular-nums">{rand(me.total_earnings_zar)}</span>
              <span className="text-sm text-muted-foreground">
                rank {me.earnings_rank ?? '—'} on earnings, {me.shifts ?? '—'} shift{me.shifts === 1 ? '' : 's'}
              </span>
            </div>
            {Number(me.prize_zar) > 0 && (
              <p className="mt-2 text-sm font-medium text-emerald-700 dark:text-emerald-300">
                {data.week_open
                  ? `If the week ended now you would win ${rand(me.prize_zar)}.`
                  : `You won ${rand(me.prize_zar)}. It is paid with your month-end salary as a performance bonus.`}
              </p>
            )}
            <div className="mt-3 grid grid-cols-3 gap-2 text-center text-xs">
              <Stat label="Per shift" value={rand(me.earnings_per_shift_zar)} />
              <Stat label="Online hours" value={fixed(me.online_hours_avg)} />
              <Stat label="Acceptance" value={percent(me.acceptance_avg)} />
              <Stat label="Per trip" value={rand(me.earnings_per_trip_zar)} />
              <Stat label="Fuel / standard" value={`${fixed(me.l_per_100km, 2)} / ${fixed(me.fuel_standard_l_per_100km, 1)}`} />
              <Stat label="Rating" value={fixed(me.rating, 2)} />
            </div>
          </>
        )}
      </section>
      )}

      {me && !me.no_shifts && (failed.length > 0 || pending.length > 0) && (
        <section className="rounded-xl border border-rose-200 bg-rose-50 p-4 dark:border-rose-900 dark:bg-rose-950/40">
          <h2 className="text-sm font-semibold">{isStaff ? 'What is blocking him' : 'What is blocking you'}</h2>
          <ul className="mt-2 space-y-3">
            {failed.map((g) => (
              <li key={g}>
                <p className="text-sm font-medium">{gateLabel(g)}</p>
                <p className="text-sm text-muted-foreground">{gateAdvice(g)}</p>
              </li>
            ))}
            {pending.map((g) => (
              <li key={g}>
                <p className="text-sm font-medium">{gateLabel(g)}: waiting for the data</p>
                <p className="text-sm text-muted-foreground">
                  This has not come through yet, so it is not counted as passed. It usually updates within a day.
                </p>
              </li>
            ))}
          </ul>
        </section>
      )}

      {me && !me.no_shifts && passed.length > 0 && (
        <section className="rounded-xl border border-border bg-card p-4">
          <h2 className="text-sm font-semibold">Rules you are meeting</h2>
          <p className="mt-1 text-sm text-muted-foreground">{passed.length} of {Object.keys(me?.gates ?? {}).length} rules.</p>
        </section>
      )}

      {me && <CoachingCard coaching={data.coaching} audience={isStaff ? 'staff' : 'driver'} />}

      {me && <ServiceHoursCard driverId={isStaff ? viewDriver : null} />}

      {/* His own speeding fines and the right to dispute them. Drivers only: staff see fines in the cockpit. */}
      {!isStaff && <MyFinesCard />}

      <section>
        <h2 className="mb-2 text-sm font-semibold">This week's board</h2>
        <div className="overflow-hidden rounded-xl border border-border bg-card">
          {data.board.map((b, i) => (
            <div
              key={i}
              onClick={isStaff && b.driver_id ? () => setViewDriver(b.driver_id!) : undefined}
              className={`${isStaff && b.driver_id ? 'cursor-pointer hover:bg-muted/60 ' : ''}flex items-center gap-3 border-t border-border px-3 py-2 text-sm first:border-t-0 ${b.is_me ? 'bg-[hsl(var(--brand-soft))]' : ''}`}
            >
              <span className="w-6 tabular-nums text-muted-foreground">{b.rank ?? '—'}</span>
              <span className="flex-1 truncate font-medium">{b.is_me ? `${b.name}${isStaff ? '' : ' (you)'}` : b.name}</span>
              <Chip tone={STANDING_TEXT[b.standing].tone}>{STANDING_TEXT[b.standing].label}</Chip>
              <span className="w-20 text-right tabular-nums">{rand(b.earnings)}</span>
            </div>
          ))}
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          Prizes {data.prizes.map((p) => rand(p)).join(', ')} go to the three highest earners who meet every rule. Prizes
          are paid with your month-end salary and show on your payslip as a performance bonus.
        </p>
      </section>

      <ZoneGuide guide={data.zone_guide} />
    </div>
  )
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md bg-muted/60 p-2">
      <p className="text-muted-foreground">{label}</p>
      <p className="mt-0.5 text-sm font-semibold tabular-nums text-foreground">{value}</p>
    </div>
  )
}

function Notice({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mt-6 rounded-xl border border-border bg-card p-6">
      <h1 className="text-base font-semibold">{title}</h1>
      <p className="mt-2 text-sm text-muted-foreground">{children}</p>
    </div>
  )
}
